import React, { useState } from 'react';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { Shield, Eye, Database, Lock, UserCheck, FileText, Mail, Phone, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
interface Section {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  content: string;
  details: string[];
}
export function PrivacyPolicyPage() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('collection');
  const sections: Section[] = [{
    id: 'collection',
    icon: <Eye className="w-12 h-12 text-blue-600" data-id="element-1165" />,
    title: 'Data Collection',
    subtitle: 'What information we gather',
    content: 'We collect personal information that you provide directly to us when you register for courses, contact us, or use our services.',
    details: ['Personal identifiers: name, email address, phone number', 'Professional information: job title, organization, industry', 'Payment information: processed securely through Paystack', 'Usage data: course progress, learning preferences, interaction patterns', 'Technical data: IP address, browser type, device information', 'Communications: messages, feedback, support requests']
  }, {
    id: 'processing',
    icon: <Database className="w-12 h-12 text-green-600" data-id="element-1166" />,
    title: 'Data Processing',
    subtitle: 'How we use your information',
    content: 'Your data is processed to deliver our services, improve your learning experience, and fulfill our contractual obligations.',
    details: ['Course delivery: provide access to learning materials and track progress', 'Communication: send course updates, certificates, and support responses', 'Payment processing: handle transactions securely via Paystack', 'Service improvement: analyze usage patterns to enhance our platform', 'Legal compliance: meet regulatory requirements under NDPR', 'Marketing: send relevant course recommendations (with your consent)']
  }, {
    id: 'storage',
    icon: <Database className="w-12 h-12 text-blue-600" data-id="element-1167" />,
    title: 'Data Storage',
    subtitle: 'Where and how long we keep your data',
    content: 'We store your information securely on cloud servers with industry-standard encryption and retain it only as long as necessary.',
    details: ['Storage location: secure cloud servers with data centers in compliance regions', 'Encryption: data encrypted in transit (SSL/TLS) and at rest (AES-256)', 'Retention period: active accounts maintained while you use our services', 'Course data: retained for 7 years after course completion for certification purposes', 'Marketing data: retained until you withdraw consent', 'Deletion: data securely deleted upon account closure or request']
  }, {
    id: 'rights',
    icon: <UserCheck className="w-12 h-12 text-green-600" data-id="element-1168" />,
    title: 'Your Rights Under NDPR',
    subtitle: 'What you can do with your data',
    content: 'Under the Nigeria Data Protection Regulation (NDPR), you have comprehensive rights over your personal information.',
    details: ['Right to access: request a copy of all personal data we hold about you', 'Right to rectification: correct inaccurate or incomplete information', 'Right to erasure: request deletion of your data (subject to legal obligations)', 'Right to restrict processing: limit how we use your data', 'Right to data portability: receive your data in a structured, machine-readable format', 'Right to object: opt-out of marketing communications and certain processing activities', 'Right to withdraw consent: revoke consent at any time without affecting prior processing', 'Right to lodge a complaint: file complaints with NITDA if you believe your rights are violated']
  }, {
    id: 'protection',
    icon: <Shield className="w-12 h-12 text-blue-600" data-id="element-1169" />,
    title: 'Data Protection',
    subtitle: 'How we keep your information safe',
    content: 'We implement robust security measures to protect your data from unauthorized access, disclosure, alteration, or destruction.',
    details: ['Technical safeguards: firewalls, intrusion detection, regular security audits', 'Access controls: role-based permissions, multi-factor authentication for staff', 'Encryption: SSL/TLS for data transmission, AES-256 for data at rest', 'Regular backups: automated daily backups with disaster recovery protocols', 'Staff training: mandatory data protection training for all employees', 'Third-party vetting: all service providers undergo security assessments', 'Incident response: documented procedures for data breach notification']
  }];
  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };
  const yourDataRights = ['Request access to your personal data', 'Correct inaccurate information', 'Request deletion of your data', 'Withdraw consent for marketing', 'Export your data in portable format', 'Lodge a complaint with NITDA'];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1170">
      <main className="flex-grow pt-20" data-id="element-1171">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-green-50 py-16 relative overflow-hidden" data-id="element-1172">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-1173" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-1174" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1175">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1176">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-green-600 rounded-full mb-6" data-id="element-1177">
                <Shield className="w-10 h-10 text-white" data-id="element-1178" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1179">
                Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 mb-4" data-id="element-1180">
                Your privacy matters to us. Learn how we collect, use, and
                protect your personal information.
              </p>
              <p className="text-sm text-gray-500" data-id="element-1181">
                Last Updated: January 2025 | Compliant with Nigeria Data
                Protection Regulation (NDPR)
              </p>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <AnimatedSection className="py-16 bg-white" data-id="element-1182">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1183">
            <div className="max-w-4xl mx-auto" data-id="element-1184">
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 border-2 border-blue-100" data-id="element-1185">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-1186">
                  <FileText className="w-6 h-6 text-blue-600" data-id="element-1187" />
                  Quick Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1188">
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1189">
                    <Eye className="w-8 h-8 text-blue-600 mb-3" data-id="element-1190" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1191">
                      What We Collect
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1192">
                      Name, email, course data, and usage information
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1193">
                    <Database className="w-8 h-8 text-green-600 mb-3" data-id="element-1194" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1195">
                      How We Use It
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1196">
                      Course delivery, communication, and service improvement
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1197">
                    <UserCheck className="w-8 h-8 text-blue-600 mb-3" data-id="element-1198" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1199">
                      Your Rights
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1200">
                      Access, correct, delete, or export your data anytime
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Visual Data Journey Timeline */}
        <AnimatedSection className="py-16 bg-gradient-to-b from-gray-50 to-white" data-id="element-1201">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1202">
            <div className="max-w-6xl mx-auto" data-id="element-1203">
              <div className="text-center mb-12" data-id="element-1204">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1205">
                  Your Data Journey
                </h2>
                <p className="text-xl text-gray-600" data-id="element-1206">
                  Follow how your information flows through our system
                </p>
              </div>

              {/* Timeline */}
              <div className="relative" data-id="element-1207">
                {/* Progress Line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-600 via-green-600 to-blue-600 hidden md:block" data-id="element-1208" />

                {sections.map((section, index) => <div key={section.id} className={`relative mb-12 ${index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:text-right'}`} data-id="element-1209">
                    <div className={`bg-white rounded-2xl shadow-xl border-2 ${activeSection === section.id ? 'border-blue-500' : 'border-gray-200'} overflow-hidden hover:shadow-2xl transition-all duration-300 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`} onMouseEnter={() => setActiveSection(section.id)} data-id="element-1210">
                      {/* Icon Circle */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden md:block" data-id="element-1211">
                        <div className="w-16 h-16 bg-white rounded-full border-4 border-blue-600 flex items-center justify-center shadow-lg" data-id="element-1212">
                          {section.icon}
                        </div>
                      </div>

                      <div className="p-8" data-id="element-1213">
                        <div className="flex items-center gap-4 mb-4 md:hidden" data-id="element-1214">
                          {section.icon}
                          <div data-id="element-1215">
                            <h3 className="text-2xl font-bold text-gray-900" data-id="element-1216">
                              {section.title}
                            </h3>
                            <p className="text-blue-600 font-medium" data-id="element-1217">
                              {section.subtitle}
                            </p>
                          </div>
                        </div>

                        <div className="hidden md:block mb-4" data-id="element-1218">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1219">
                            {section.title}
                          </h3>
                          <p className="text-blue-600 font-medium" data-id="element-1220">
                            {section.subtitle}
                          </p>
                        </div>

                        <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1221">
                          {section.content}
                        </p>

                        <button onClick={() => toggleSection(section.id)} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200" data-id="element-1222">
                          {expandedSection === section.id ? <>
                              <ChevronUp className="w-5 h-5" data-id="element-1223" />
                              Show Less
                            </> : <>
                              <ChevronDown className="w-5 h-5" data-id="element-1224" />
                              Learn More
                            </>}
                        </button>

                        {expandedSection === section.id && <div className="mt-6 pt-6 border-t border-gray-200 space-y-3 animate-fadeIn" data-id="element-1225">
                            {section.details.map((detail, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1226">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1227" />
                                <p className="text-gray-700 text-sm" data-id="element-1228">
                                  {detail}
                                </p>
                              </div>)}
                          </div>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Your Data Rights Checklist */}
        <AnimatedSection className="py-16 bg-white" data-id="element-1229">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1230">
            <div className="max-w-4xl mx-auto" data-id="element-1231">
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border-2 border-green-100" data-id="element-1232">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-1233">
                  <Lock className="w-8 h-8 text-green-600" data-id="element-1234" />
                  Your Data Rights Checklist
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-1235">
                  Under NDPR, you have the following rights. Check what you can
                  do:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1236">
                  {yourDataRights.map((right, index) => <div key={index} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center gap-3" data-id="element-1237">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1238">
                        <CheckCircle className="w-4 h-4 text-white" data-id="element-1239" />
                      </div>
                      <p className="text-gray-700 font-medium" data-id="element-1240">{right}</p>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Contact Section */}
        <AnimatedSection className="py-16 bg-gradient-to-b from-gray-50 to-white" data-id="element-1241">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1242">
            <div className="max-w-4xl mx-auto" data-id="element-1243">
              <div className="bg-gradient-to-br from-blue-600 to-green-600 rounded-2xl p-8 text-white text-center" data-id="element-1244">
                <h2 className="text-3xl font-bold mb-4" data-id="element-1245">
                  Questions About Your Privacy?
                </h2>
                <p className="text-blue-100 mb-8 text-lg" data-id="element-1246">
                  Our Data Protection Officer is here to help. Contact us for
                  any privacy-related inquiries.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto" data-id="element-1247">
                  <a href="mailto:support@myskillzhq.com" className="bg-white text-blue-600 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-id="element-1248">
                    <Mail className="w-8 h-8 mx-auto mb-3" data-id="element-1249" />
                    <p className="font-bold mb-1" data-id="element-1250">Email Us</p>
                    <p className="text-sm" data-id="element-1251">support@myskillzhq.com</p>
                  </a>
                  <a href="tel:+2348106836707" className="bg-white text-green-600 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-id="element-1252">
                    <Phone className="w-8 h-8 mx-auto mb-3" data-id="element-1253" />
                    <p className="font-bold mb-1" data-id="element-1254">Call Us</p>
                    <p className="text-sm" data-id="element-1255">0810 683 6707</p>
                  </a>
                </div>
                <p className="text-blue-100 text-sm mt-8" data-id="element-1256">
                  SkillzHQ is committed to protecting your privacy in compliance
                  with the Nigeria Data Protection Regulation (NDPR) and
                  international best practices.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}